// Page titles and link-preview metadata — the text half of Block J's unfurl.
//
// A shared /g/ link currently arrives in WhatsApp, Slack or a group chat as a bare
// URL, and the whole growth loop is people sending each other those links. The
// reason is D7, not a bug: the artwork does not exist until a browser computes it,
// and no crawler runs our JavaScript. But a *title* and a *description* need no
// renderer at all — they are derivable from the token itself — so they ship first
// and independently of the og:image, which is gated on the workerd spike.
//
// This module is deliberately PURE and browser-free, because it runs in two places
// that share no globals:
//   - the Worker, which rewrites the served HTML for /g/* so a crawler sees it;
//   - the browser, which sets document.title on client-side navigation, where no
//     HTML is ever fetched and the Worker therefore never runs.
// One implementation, the same habit as coordId (D3): two would drift, and the
// drift would be invisible from inside the product — exactly F1's failure mode.
//
// Nothing here echoes the URL back. Every value is DERIVED from decoded bounds,
// and the canonical link is rebuilt by re-encoding them, so a mangled or hostile
// token cannot reach the page as markup. That is a property worth keeping when the
// og:image handler starts emitting tags of its own.

import { decodeShareToken, encodeShareToken } from './shareId.js';
import coordId from './coordId.js';
import { magnification, shortId } from './format.js';

export const SITE_NAME = 'FractalArt';

// The site-wide defaults. These are ALSO baked into public/index.html, which is
// what /, /explore and /gallery serve to a crawler — the Worker only overrides
// them for /g/*. Keep the two in step; index.html is the copy that survives if
// this code never runs.
export const SITE_TITLE = 'FractalArt — find the one that’s yours';
export const SITE_DESCRIPTION =
    'Zoom into the Mandelbrot set until something stops you. Claim its coordinates, ' +
    'and no one else can have them.';

// The unfurl a stranger reads in a chat window, in the share page's own voice
// ("Someone found this one. Go and find yours."). Two titles, not one, because
// they are read in different places: `title` is a browser tab, a bookmark and a
// search result, so it carries the site name; `ogTitle` is a preview headline
// sitting directly beneath og:site_name, where repeating it is noise.
//
// Returns null for anything that isn't a specimen, so callers fall back to the
// site defaults rather than titling a broken link with half a sentence.
export const specimenMeta = (token) => {
    const bounds = decodeShareToken(token);
    if (!bounds) return null;

    const id = coordId(bounds);
    const short = shortId(id);
    const factor = magnification(bounds);

    // The colon form of a coordId is accepted on input (shareId.js), so the token
    // in the URL is not necessarily the one we would have written. Re-encoding the
    // decoded bounds gives the canonical 43-char form — which is what og:url and
    // rel=canonical should name, and is why no input ever reaches the markup.
    const canonicalToken = encodeShareToken(bounds);

    return {
        id,
        short,
        factor,
        canonicalToken,
        title: `Specimen ${short} — ${SITE_NAME}`,
        ogTitle: factor
            ? `Specimen ${short}, ×${factor} magnification`
            : `Specimen ${short}`,
        description: factor
            ? `Someone found this one, ${factor}× deep in the Mandelbrot set. Your ` +
              `browser will recompute it from the coordinates in this link. Then go ` +
              `and find yours.`
            : `Someone found this one. Your browser will recompute it from the ` +
              `coordinates in this link. Then go and find yours.`
    };
};

// Every route's title and description, from a pathname alone.
//
// The unknown-path case answers as the 404 rather than as the site, because
// `not_found_handling: single-page-application` means a bad URL is served the SPA
// shell with a 200 — App.js's catch-all route is the only thing that knows it is a
// 404, and this is the metadata half of that same job.
export const metaForPath = (pathname) => {
    const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;

    if (path === '/') return { title: SITE_TITLE, description: SITE_DESCRIPTION };

    if (path === '/explore') {
        return {
            title: `Generator — ${SITE_NAME}`,
            description: SITE_DESCRIPTION
        };
    }

    if (path === '/gallery') {
        return {
            title: `Gallery — ${SITE_NAME}`,
            description: 'The specimens you have saved.'
        };
    }

    if (path.startsWith('/g/')) {
        const meta = specimenMeta(path.slice('/g/'.length));
        if (meta) return meta;
        return {
            title: `Specimen not found — ${SITE_NAME}`,
            description: 'That link doesn’t point at a specimen.'
        };
    }

    return {
        title: `Not found — ${SITE_NAME}`,
        description: SITE_DESCRIPTION
    };
};
